import { useCallback, useEffect, useState } from 'react';
import { Plus } from '../../icons';
import { PageHeader } from '../../components/shared/PageHeader/PageHeader';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { Table } from '../../components/shared/Table/Table';
import { Button } from '../../components/shared/Button/Button';
import { Input } from '../../components/shared/Input/Input';
import { FormField } from '../../components/shared/FormField/FormField';
import { Modal, ModalActions, ModalFootNote } from '../../components/shared/Modal/Modal';
import { extractErrorMessage } from '../../api/approval';
import {
  listApprovalBoxes, createApprovalBox, updateApprovalBox, type ApprovalBoxDto,
} from '../../api/approval-box';
import './ApprovalBoxes.css';

/**
 * 결재함 — 회사가 관리하는 **전사결재함** (2026-09-07 · 시안 A1 · 사용자 결정).
 *
 * ── 무엇인가 ────────────────────────────────────────────────────────────
 * 「결재양식」 창의 **왼쪽 묶음**이 이 목록이다. 양식마다 함을 하나 정해 두면(「양식 · 서식」의
 * 「결재함」 칸), 그 양식으로 올린 문서가 그 함에 들어간다.
 *
 * ── ★★★ 일곱은 «씨앗»이고 표는 비어 있는 것이 정상이다 ──────────────────
 * 출장·인사·일반·회계·근태·지원·복지는 **서버 코드의 카탈로그**가 갖는다. 회사가 이름을
 * 고치거나 순서를 바꾸거나 끌 때 비로소 행이 생긴다 — 양식 카탈로그와 같은 방식이다.
 * 그래서 목록에 `id === null` 인 줄이 섞여 오고, 그것들도 **똑같이 고칠 수 있다**(코드로 고친다).
 *
 * ── ★★ 지우지 않는다 ───────────────────────────────────────────────────
 * 그 함에 든 문서가 있다. 「사용」을 끄면 **새 양식을 못 붙일 뿐**, 이미 그 함에 있는 문서는
 * 그대로 남는다.
 */
export function ApprovalBoxesPage(): JSX.Element {
  const [items, setItems] = useState<ApprovalBoxDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ code: string; name: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await listApprovalBoxes());
    } catch (err) {
      setError(extractErrorMessage(err));
      setItems([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patch = useCallback(async (code: string, p: Parameters<typeof updateApprovalBox>[1]) => {
    setBusy(true); setError(null);
    try {
      await updateApprovalBox(code, p);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally { setBusy(false); }
  }, [load]);

  const create = useCallback(async () => {
    if (!draft) return;
    setBusy(true); setError(null);
    try {
      await createApprovalBox({ code: draft.code.trim().toUpperCase(), name: draft.name.trim() });
      setDraft(null);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally { setBusy(false); }
  }, [draft, load]);

  return (
    <div className="abox">
      <PageHeader
        breadcrumb={crumbsFor('/approval/admin/boxes')}
        scope={scopeFor('/approval/admin/boxes')}
        title="결재함 설정"
        description="「결재양식」 창의 왼쪽 묶음이자, 그 양식으로 올린 문서가 들어갈 전사결재함입니다."
        actions={(
          <Button
            variant="primary" size="sm" iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => setDraft({ code: '', name: '' })}
          >
            결재함 만들기
          </Button>
        )}
        help={{
          title: '결재함',
          body: (
            <>
              <p>
                일곱(출장 · 인사 · 일반 · 회계 · 근태 · 지원 · 복지)은 <b>처음부터 있습니다.</b>
                이름을 고치거나 순서를 바꾸면 그때부터 이 회사의 값이 됩니다.
              </p>
              <p>
                <b>지우는 길은 없습니다.</b> 그 함에 든 문서가 있기 때문입니다 —
                쓰지 않게 된 함은 <b>사용</b>을 꺼 주세요. 끄면 새 양식을 못 붙일 뿐,
                이미 그 함에 있는 문서는 그대로 남습니다.
              </p>
              <p>
                양식을 어느 함에 넣을지는 <b>「양식 · 서식」의 「결재함」 칸</b>에서 정합니다.
              </p>
            </>
          ),
        }}
      />

      {error && <p className="abox__err" role="alert">{error}</p>}

      <Table<ApprovalBoxDto>
        caption="회사의 결재함 목록"
        rows={items ?? []}
        rowKey={(r) => r.code}
        columns={[
          {
            key: 'name',
            header: '이름',
            rowHeader: true,
            render: (r) => (
              <Input
                size="sm"
                defaultValue={r.name}
                maxLength={50}
                disabled={busy}
                aria-label={`${r.name} 이름`}
                wrapperClassName="abox__nm"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v === '' || v === r.name) { e.target.value = r.name; return; }
                  void patch(r.code, { name: v });
                }}
              />
            ),
          },
          {
            key: 'code',
            header: '코드',
            width: '150px',
            // ★ 코드는 **보여 주되 못 고친다** — 양식과 문서가 이 값을 들고 있다.
            render: (r) => <span className="abox__code">{r.code}</span>,
          },
          {
            key: 'sortOrder',
            header: '순서',
            width: '90px',
            numeric: true,
            render: (r) => (
              <Input
                size="sm"
                type="number"
                min={0}
                max={9999}
                defaultValue={r.sortOrder}
                disabled={busy}
                aria-label={`${r.name} 순서`}
                wrapperClassName="abox__ord"
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isInteger(v) || v < 0 || v > 9999 || v === r.sortOrder) {
                    e.target.value = String(r.sortOrder);
                    return;
                  }
                  void patch(r.code, { sortOrder: v });
                }}
              />
            ),
          },
          {
            key: 'origin',
            header: '출처',
            width: '110px',
            render: (r) => (
              <span className={`abox__tag${r.origin === 'COMPANY' ? ' is-own' : ''}`}>
                {r.origin === 'COMPANY' ? '우리 회사' : '기본'}
              </span>
            ),
          },
          {
            key: 'use',
            header: '사용',
            width: '110px',
            render: (r) => (
              <Button
                variant={r.isActive ? 'secondary' : 'primary'}
                size="sm"
                disabled={busy}
                onClick={() => void patch(r.code, { isActive: !r.isActive })}
              >
                {r.isActive ? '중지' : '사용'}
              </Button>
            ),
          },
        ]}
      />

      {items !== null && items.length === 0 && !error && (
        <p className="abox__none">결재함을 불러오지 못했습니다.</p>
      )}

      {draft && (
        <Modal
          title="결재함 만들기"
          size="sm"
          onClose={() => setDraft(null)}
          footer={(
            <>
              <ModalFootNote>코드는 만든 뒤에 바꿀 수 없습니다.</ModalFootNote>
              <ModalActions>
                <Button variant="secondary" size="lg" onClick={() => setDraft(null)}>취소</Button>
                <Button
                  variant="primary" size="lg"
                  disabled={busy || draft.name.trim() === '' || !/^[A-Z][A-Z0-9_]{1,29}$/.test(draft.code.trim().toUpperCase())}
                  onClick={() => void create()}
                >
                  만들기
                </Button>
              </ModalActions>
            </>
          )}
        >
          <FormField label="이름" required>
            <Input
              fullWidth autoFocus maxLength={50}
              value={draft.name}
              placeholder="영업"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </FormField>
          <FormField label="코드" required hint="대문자·숫자·밑줄. 만든 뒤에는 바꿀 수 없습니다.">
            <Input
              fullWidth maxLength={30}
              value={draft.code}
              placeholder="SALES"
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            />
          </FormField>
        </Modal>
      )}
    </div>
  );
}
